/**
 * Shared schedule-day utilities for the MOOP Ad Sales surfaces.
 *
 * mergeConsecutiveDays — groups Date[] into contiguous {start,end} runs.
 *   Moved from CreateAdSales/mockData.ts so all callers share one definition.
 *
 * expandBookingSlotsToDays — converts bookingSlots to a deduped UTC-day Date[].
 *   Handles all three producer shapes:
 *     MOOP merged ranges  (00:00–23:59:59.999, multi-day)  → expands all days
 *     admin per-day       (d 00:00–d 23:59:59)             → ≤ 24h → day d only
 *     admin time-of-day   (07 08:00–08 08:00)              → exactly 24h → day 07 only
 *
 * buildDayRangeSegments — formats Date[] into display range segments.
 *   Returns { segments: string[], year: string } where segments are formatted
 *   per the approved treatment (single day / same-month run / cross-month run)
 *   and year is the year of the last day. Callers append year to the last
 *   visible segment.
 */

const DAY_MS = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDay(d: Date): string {
  const day = String(d.getUTCDate()).padStart(2, '0');
  const mon = MONTHS[d.getUTCMonth()];
  return `${day} ${mon}`;
}

/**
 * Groups Date[] into maximal consecutive runs.
 * Returns [{start, end}] sorted by start ascending.
 * Used to build bookingSlots for the API request.
 */
export function mergeConsecutiveDays(days: Date[]): Array<{ start: Date; end: Date }> {
  if (days.length === 0) return [];
  const sorted = [...days].sort((a, b) => a.getTime() - b.getTime());
  const runs: Array<{ start: Date; end: Date }> = [];
  let runStart = sorted[0];
  let runEnd = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]);
    prev.setUTCHours(0, 0, 0, 0);
    const curr = new Date(sorted[i]);
    curr.setUTCHours(0, 0, 0, 0);
    if (curr.getTime() - prev.getTime() <= DAY_MS) {
      runEnd = sorted[i];
    } else {
      runs.push({ start: runStart, end: runEnd });
      runStart = sorted[i];
      runEnd = sorted[i];
    }
  }
  runs.push({ start: runStart, end: runEnd });
  return runs;
}

/**
 * Converts bookingSlots to a deduped, sorted UTC-day Date[].
 *
 * Decision rule per slot:
 *   if (endTime − startTime) ≤ 24h → single day = UTC-floor(startTime)
 *   else expand UTC-floor(startTime)..UTC-floor(endTime − 1ms) day by day
 *
 * The ≤ 24h rule catches both admin per-day slots (d 00:00 → d 23:59:59, ≈86399s)
 * and admin time-of-day slots (07 08:00 → 08 08:00, exactly 86400s), keeping only
 * the start day for both. Multi-day MOOP ranges (> 24h) expand correctly.
 */
export function expandBookingSlotsToDays(slots: Array<{ startTime: string; endTime: string }>): Date[] {
  const seen = new Set<string>();
  const days: Date[] = [];

  for (const slot of slots) {
    const startMs = new Date(slot.startTime).getTime();
    const endMs = new Date(slot.endTime).getTime();
    const diffMs = endMs - startMs;

    if (diffMs <= DAY_MS) {
      const d = new Date(startMs);
      d.setUTCHours(0, 0, 0, 0);
      const key = d.toISOString().slice(0, 10);
      if (!seen.has(key)) {
        seen.add(key);
        days.push(d);
      }
    } else {
      let cur = new Date(startMs);
      cur.setUTCHours(0, 0, 0, 0);
      const last = new Date(endMs - 1);
      last.setUTCHours(0, 0, 0, 0);
      while (cur.getTime() <= last.getTime()) {
        const key = cur.toISOString().slice(0, 10);
        if (!seen.has(key)) {
          seen.add(key);
          days.push(new Date(cur));
        }
        cur = new Date(cur.getTime() + DAY_MS);
      }
    }
  }

  days.sort((a, b) => a.getTime() - b.getTime());
  return days;
}

/**
 * Formats a Date[] into display range segments.
 *
 * Segment formats:
 *   single day:       "07 Jul"
 *   same-month run:   "07 – 11 Jul"
 *   cross-month run:  "28 Jul – 02 Aug"
 *
 * Year of the last day is returned separately; callers append it to the
 * last visible segment (e.g. "07 – 11 Jul 2026").
 *
 * Returns { segments: [], year: '' } for empty input.
 */
export function buildDayRangeSegments(days: Date[]): { segments: string[]; year: string } {
  if (days.length === 0) return { segments: [], year: '' };

  // Dedupe by YYYY-MM-DD, sort ascending
  const seen = new Set<string>();
  const clean: Date[] = [];
  for (const d of days) {
    const utc = new Date(d);
    utc.setUTCHours(0, 0, 0, 0);
    const key = utc.toISOString().slice(0, 10);
    if (!seen.has(key)) {
      seen.add(key);
      clean.push(utc);
    }
  }
  clean.sort((a, b) => a.getTime() - b.getTime());

  const runs = mergeConsecutiveDays(clean);
  const year = String(clean[clean.length - 1].getUTCFullYear());

  const segments = runs.map(({ start, end }) => {
    if (start.toISOString().slice(0, 10) === end.toISOString().slice(0, 10)) {
      return fmtDay(start);
    }
    if (start.getUTCMonth() === end.getUTCMonth()) {
      // "07 – 11 Jul"
      const startDay = String(start.getUTCDate()).padStart(2, '0');
      const endDay = String(end.getUTCDate()).padStart(2, '0');
      return `${startDay} – ${endDay} ${MONTHS[end.getUTCMonth()]}`;
    }
    // "28 Jul – 02 Aug"
    return `${fmtDay(start)} – ${fmtDay(end)}`;
  });

  return { segments, year };
}
